import React from 'react';
import { useLocalStore, useObserver } from 'mobx-react';
import { runInAction } from 'mobx';
import { _COMPARISON_CHART_INIT } from './constants';
import BasketballMatchRelativeRecordModel from './model/record';
import BasketballMatchTeamStatModel from './model/teamStat';
import BasketballMatchRankModel from './model/rank';
import { ScoreUtil } from '../../../../../common/utils/scoreUtil';
import { ObjectUtil } from '../../../../../common/utils/objectUtil';
import { ScoreDetailService } from '../../../../../common/services';

const BasketballMatchRelativeRecordStore = () => {
  const store = useLocalStore(() => ({
    playerStat: {
      home: {},
      away: {}
    },
    record: {
      recentAwayRecord: [],
      recentHomeRecord: [],
      vsRecord: [],
      selectedRecentRecordTab: 'home'
    },
    teamStat: {
      home: {},
      away: {}
    },
    rank: [],
    loading: true,
    init: false,
    gameId: null,
    get vsRecordSummary() {
      const { vsRecord } = store.record;
      const { playerStat } = store;
      const vsCount = vsRecord
        .slice()
        .map((item) => ScoreUtil.getSummaryScore(item, playerStat.home.id))
        .reduce(
          (a, c) => {
            if (c.home.score > c.away.score) {
              a.win++;
            } else if (c.away.score > c.home.score) {
              a.loss++;
            }

            return a;
          },
          { win: 0, loss: 0 }
        );
      const typeCount = vsRecord
        .slice()
        .map((item) =>
          ScoreUtil.getOddsTypeWinnerCount(item, playerStat.home.id)
        )
        .reduce(
          (a, c) => {
            if (c.handicap.win.length > 0) {
              a.h.w++;
            }
            if (c.handicap.loss.length > 0) {
              a.h.l++;
            }
            if (c.unover.win.length > 0) {
              a.u.w++;
            }
            if (c.unover.loss.length > 0) {
              a.u.l++;
            }

            return a;
          },
          { h: { w: 0, l: 0 }, u: { w: 0, l: 0 } }
        );

      let result = {
        vs: '',
        handicap: { text: '', red: '' },
        unover: { text: '', red: '' },
        home: { visible: false, text: '', bold: '' },
        away: { visible: false, text: '', bold: '' },
        win: { visible: false, text: '' }
      };

      if (vsRecord.length > 0) {
        result.vs = `홈팀(${store.teamStat.home.name})이 ${vsCount.win}승 ${vsCount.loss}패`;
        result.handicap = {
          text: `홈팀(${store.teamStat.home.name}) 기준 ${vsRecord.length}경기 중 `,
          red: `핸디 승 ${typeCount.h.w}회, 핸디 패 ${typeCount.h.l}회`
        };
        result.unover = {
          text: `홈팀(${store.teamStat.home.name}) 기준 ${vsRecord.length}경기 중 `,
          red: `언더 ${typeCount.u.w}회 오버 ${typeCount.u.l}회`
        };

        const winningCountArr = vsRecord
          .slice()
          .map((item) => ScoreUtil.getSummaryScore(item, playerStat.home.id));
        let winningCount = 0;
        let lossCount = 0;
        let countText = '';

        winningCountArr.some((item) => {
          if (item.home.score > item.away.score) {
            winningCount++;
          }

          return item.away.score > item.home.score;
        });

        winningCountArr.some((item) => {
          if (item.away.score > item.home.score) {
            lossCount++;
          }

          return item.home.score > item.away.score;
        });

        if (winningCount > 0) {
          countText = `${winningCount}연승 중입니다.`;
        } else {
          countText = `${lossCount}연패 중입니다.`;
        }

        result.win = {
          text: `최근 홈팀(${store.teamStat.home.name})이 원정팀(${store.teamStat.away.name})을 상대로 ${countText}`,
          visible: true
        };
      }

      return result;
    },
    get recentRecordSummary() {
      const {
        recentAwayRecord,
        recentHomeRecord,
        selectedRecentRecordTab
      } = store.record;
      const { playerStat } = store;
      let record = recentHomeRecord;

      let result = {
        total: { text: '', red: '' },
        type: { text: '', red: '' },
        win: { text: '', bold: '' }
      };

      if (selectedRecentRecordTab === 'away') {
        record = recentAwayRecord;
      }

      const count = record
        .slice()
        .map((item) =>
          ScoreUtil.getSummaryScore(
            item,
            playerStat[selectedRecentRecordTab].id
          )
        )
        .reduce(
          (a, c) => {
            if (c.home.score > c.away.score) {
              a.win++;
            } else if (c.away.score > c.home.score) {
              a.loss++;
            }

            return a;
          },
          { win: 0, loss: 0 }
        );

      const typeCount = record
        .slice()
        .map((item) =>
          ScoreUtil.getOddsTypeWinnerCount(
            item,
            playerStat[selectedRecentRecordTab].id
          )
        )
        .reduce(
          (a, c) => {
            if (c.handicap.win.length > 0) {
              a.h.w++;
            }
            if (c.handicap.loss.length > 0) {
              a.h.l++;
            }
            if (c.unover.win.length > 0) {
              a.u.w++;
            }
            if (c.unover.loss.length > 0) {
              a.u.l++;
            }

            return a;
          },
          { h: { w: 0, l: 0 }, u: { w: 0, l: 0 } }
        );

      result.total = {
        text: `${selectedRecentRecordTab === 'home' ? '홈' : '원정'}팀(${
          playerStat[selectedRecentRecordTab].name
        })은 `,
        red: `최근 ${record.length}경기 ${count.win}승 ${count.loss}패`
      };
      result.type = {
        text: `${record.length}경기 중 `,
        red: `핸디 승 ${typeCount.h.w}회/핸디 패 ${typeCount.h.l}회, 언더 ${typeCount.u.w}회/오버 ${typeCount.u.l}회`
      };

      const winningCountArr = record
        .slice()
        .map((item) =>
          ScoreUtil.getSummaryScore(
            item,
            playerStat[selectedRecentRecordTab].id
          )
        );
      let winningCount = 0;
      let lossCount = 0;
      let countText = '';

      winningCountArr.some((item) => {
        if (item.home.score > item.away.score) {
          winningCount++;
        }

        return item.away.score > item.home.score;
      });

      winningCountArr.some((item) => {
        if (item.away.score > item.home.score) {
          lossCount++;
        }

        return item.home.score > item.away.score;
      });

      if (winningCount > 0) {
        countText = `${winningCount}연승 중입니다.`;
      } else {
        countText = `${lossCount}연패 중입니다.`;
      }

      result.win = {
        text: `${selectedRecentRecordTab === 'home' ? '홈' : '원정'}팀(${
          playerStat[selectedRecentRecordTab].name
        })은 최근 ${countText}`,
        visible: true
      };
      // result.type

      return result;
    },
    get comparisonChart() {
      const { teamStat } = store;
      const { home, away } = teamStat;

      let result = ObjectUtil.deepClone(_COMPARISON_CHART_INIT);

      if (!!home.chart && !!away.chart) {
        Object.keys(_COMPARISON_CHART_INIT).forEach((key) => {
          result[key] = {
            home: home.chart[key],
            away: away.chart[key],
            visible: false
          };

          if (result[key]['home'].p > 0 && result[key]['away'].p > 0) {
            result[key]['visible'] = true;

            const homeP = result[key]['home'].p;
            const awayP = result[key]['away'].p;

            result[key]['home']['active'] = false;
            result[key]['away']['active'] = false;
            result[key]['visible'] = true;

            if (key === 'pointsLossAverage' || key === 'turnoversAverage') {
              if (homeP > awayP) {
                result[key]['away']['active'] = true;
              } else {
                result[key]['home']['active'] = true;
              }
            } else {
              if (homeP > awayP) {
                result[key]['home']['active'] = true;
              } else {
                result[key]['away']['active'] = true;
              }
            }

            if (homeP === awayP) {
              result[key]['home']['active'] = false;
              result[key]['away']['active'] = false;
            }

            if (result[key]['home'].v === result[key]['away'].v) {
              result[key]['home']['active'] = false;
              result[key]['away']['active'] = false;
            }
          }
        });
      }

      return result;
    },
    async getPlayerStat(gameId) {
      const result = await ScoreDetailService.getPlayerSeasonStatBySportsType(
        'basketball',
        gameId
      );

      if (!!result) {
        return result;
      }
    },
    async getRecord(gameId) {
      const result = await ScoreDetailService.getRecordBySportsType(
        'basketball',
        gameId
      );

      if (!!result) {
        return result;
      }
    },
    async getRank(gameId) {
      const result = await ScoreDetailService.getRankBySportsType(
        'basketball',
        gameId
      );

      if (!!result) {
        return result;
      }
    },
    async getTeamStat(gameId) {
      const result = await ScoreDetailService.getTeamSeasonStatBySportsType(
        'basketball',
        gameId
      );

      if (!!result) {
        return result;
      }
    },
    getAll: async (gameId) => {
      store.loading = true;

      const [playerStat, record, teamStat, rank] = await Promise.all([
        store.getPlayerStat(gameId),
        store.getRecord(gameId),
        store.getTeamStat(gameId),
        store.getRank(gameId)
      ]);

      runInAction(() => {
        store.playerStat = {
          home: playerStat.home,
          away: playerStat.away
        };
        store.record = new BasketballMatchRelativeRecordModel(
          record,
          teamStat.home,
          teamStat.away
        );
        store.teamStat = {
          home: new BasketballMatchTeamStatModel(teamStat.home, 'home'),
          away: new BasketballMatchTeamStatModel(teamStat.away, 'away')
        };
        store.rank = new BasketballMatchRankModel(
          rank,
          teamStat.home.id,
          teamStat.away.id
        );
        store.loading = false;
      });
    }
  }));

  return {
    getAll: store.getAll,
    data: useObserver(() => {
      return {
        init: store.init,
        loading: store.loading,
        playerStat: store.playerStat,
        record: store.record,
        vsRecordSummary: store.vsRecordSummary,
        recentRecordSummary: store.recentRecordSummary,
        teamStat: store.teamStat,
        comparisonChart: store.comparisonChart,
        rank: store.rank
      };
    })
  };
};

export default BasketballMatchRelativeRecordStore;
